import {forwardRef, useContext, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomPanel, {atomPanelColors, atomPanelRounded} from '@s-ui/react-atom-panel'
import Context from '@s-ui/react-context'
import {useLocation} from '@s-ui/react-router'
import {
  getImagesUrlsAdapted,
  imageUrlAdapterAspectRatios,
  imageUrlAdapterTransformTypes
} from '@adv-mt/mt-hook-use-image-url-adapter'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import RatingBasic, {ratingBasicDesigns} from '@adv-mt/mt-rating-basic'
import IconDirectionsOutline from '@adv-mt/mt-svg-icons/lib/DirectionsOutline'
import IconLinkExternal from '@adv-mt/mt-svg-icons/lib/LinkExternal'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

import ActionButtons from './components/ActionButtons.js'
import ContactButtons from './components/ContactButtons.js'
import DealerImagePreview from './components/DealerImagePreview.js'
import PanelContactButtons from './components/PanelContactButton.js'
import {DESIGN, LOGO_IMG_HEIGHT, LOGO_IMG_WIDTH, NO_OP, TYPE} from './config.js'
import {mapAddress} from './helpers.js'

const LOGO_ADAPTER_OPTIONS = {
  aspectRatio: imageUrlAdapterAspectRatios.WIDE_SCREEN,
  imageWidth: LOGO_IMG_WIDTH * 2, // 2x for HiDPI screens
  transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
}

const HeroDealer = forwardRef(
  (
    {
      titleAsLink = false,
      adCategoryId,
      adId,
      dealer = {},
      design = DESIGN.DEFAULT,
      extendedLeadTrackingData,
      isFavorite,
      // Experiment ff_web_buyer_stock_share_button
      isShareDealerEnabled,
      isHidePhone = false,
      isLoggedUser,
      isRentingDealer,
      linkFactory: Link,
      multiProvinceTrackingData = 'unknown',
      onClickGoToRatings = NO_OP,
      onClickOpenModal = NO_OP,
      pageName,
      type = TYPE.DEFAULT
    },
    forwardedRef
  ) => {
    const {i18n, domain} = useContext(Context)
    const config = domain.get('config')
    const {DEALER_PACKS} = config.get('dealer')
    const {ADS_LIST_SUBSCRIPTION_PATH_PATTERN} = config.get('page')
    const {SHOW_DEALER_RATING} = config.get('search')

    const {pathname} = useLocation()
    const isRenting = Boolean(pathname.includes(ADS_LIST_SUBSCRIPTION_PATH_PATTERN))
    const {
      id,
      isActive,
      externalPageUrl,
      location = {},
      name = '',
      pack,
      ratings,
      storeImageUrls = [],
      videos = []
    } = dealer
    const {mapsDirectionsUrl} = location || {}
    const showDealerRating = SHOW_DEALER_RATING && !isRenting

    const [logoImageUrl, setLogoImageUrl] = useState(() => {
      const [adapted] = getImagesUrlsAdapted({images: [{src: dealer.logoImageUrl}], options: LOGO_ADAPTER_OPTIONS})
      return adapted?.src ?? dealer.logoImageUrl
    })

    const isTabletResolution = useMediaQuery(useMediaQueryBreakpoints.md)

    const {address, city, postalCode} = location
    const hasLogo = Boolean(logoImageUrl)

    const hasRatings = Boolean(ratings)

    const hasMapsDirectionsUrl = Boolean(mapsDirectionsUrl)

    const fullAddress = mapAddress({address, city, postalCode})

    const isDefaultType = type === TYPE.DEFAULT
    const isDesignDefault = design === DESIGN.DEFAULT

    const logoAlt = i18n.t('COMMON.DEALER_LOGO', {
      dealerName: name
    })

    const isSubscriptionDealer = pack === DEALER_PACKS.SUBSCRIPTION

    const showFullAddress = !isSubscriptionDealer

    const dealerImage = storeImageUrls[0]

    const heroDealerClassName = cx('mt-HeroDealer', {
      'mt-HeroDealer--compressed': design === DESIGN.COMPRESSED
    })

    const contentClassName = cx('mt-HeroDealer-content', {
      'mt-HeroDealer-content--withoutImage': !dealerImage,
      'mt-HeroDealer-content--compressed': design === DESIGN.COMPRESSED
    })

    const infoClassName = cx('mt-HeroDealer-info', {
      'mt-HeroDealer-info--compressed': design === DESIGN.COMPRESSED
    })

    const firstBlockClassName = cx('mt-HeroDealer-firstBlock', {
      'mt-HeroDealer-firstBlock--compressed': design === DESIGN.COMPRESSED
    })

    const panelProps = {
      color: atomPanelColors.CANVAS,
      rounded: atomPanelRounded.M
    }

    const titleColorProps = {
      color: titleBasicColors.white,
      descriptionColor: titleBasicColors.white
    }

    const title = `${name} ${isSubscriptionDealer ? i18n.t('COMMON.RENTING') : ''}`.trim()

    const titleSize = isTabletResolution ? titleBasicSizes.l : titleBasicSizes.s

    const handleClickGoToRatings = () => {
      onClickGoToRatings()
    }

    const removeImage = () => setLogoImageUrl(null)

    return (
      <AtomPanel {...panelProps}>
        <div className={heroDealerClassName} ref={forwardedRef}>
          <div className={contentClassName}>
            <div className={infoClassName}>
              <div className={firstBlockClassName}>
                {(hasLogo || showDealerRating) && (
                  <div className={`${firstBlockClassName}-wrapper`}>
                    {hasLogo && (
                      <img
                        src={logoImageUrl}
                        alt={logoAlt}
                        className="mt-HeroDealer-logo"
                        height={LOGO_IMG_HEIGHT}
                        onError={removeImage}
                      />
                    )}
                    {showDealerRating && (
                      <RatingBasic
                        design={ratingBasicDesigns.LIGHT}
                        label={
                          hasRatings
                            ? i18n.t('COMMON.PLURALS.RATING_COUNT', ratings.totalReviews)
                            : i18n.t('DEALER_RATINGS.EMPTY')
                        }
                        onClickLabel={handleClickGoToRatings}
                        value={ratings?.average}
                      />
                    )}
                  </div>
                )}
                {isDesignDefault && (
                  <div className={`${firstBlockClassName}-actions`}>
                    <ActionButtons
                      dealer={dealer}
                      isFavorite={isFavorite}
                      isShareDealerEnabled={isShareDealerEnabled}
                      className="mt-HeroDealer-buttons-action-mobile"
                      hideText
                    />
                  </div>
                )}
              </div>
              <div>
                {titleAsLink ? (
                  <Link className="mt-HeroDealer-titleLink" href={dealer.stockPageUrl}>
                    {title}
                  </Link>
                ) : (
                  <TitleBasic as={titleBasicTags.h1} size={titleSize} title={title} {...titleColorProps} />
                )}
                {!isDesignDefault && showFullAddress && <p className="mt-HeroDealer-address">{fullAddress}</p>}
                {isDesignDefault && (
                  <div className="mt-HeroDealer-containerInfo">
                    {showFullAddress && (
                      <div className="mt-HeroDealer-containerAddress">
                        <IconDirectionsOutline />
                        <div className="mt-HeroDealer-containerInfoAddressWrapper">
                          {hasMapsDirectionsUrl && (
                            <a
                              className="mt-HeroDealer-containerInfoAddress"
                              href={mapsDirectionsUrl}
                              rel="noopener noreferrer"
                              target="_blank"
                              title={fullAddress}
                            >
                              {i18n.t('DEALER.ADDRESS_INDICATIONS')}
                            </a>
                          )}
                          <p className="mt-HeroDealer-address">{fullAddress}</p>
                        </div>
                      </div>
                    )}
                    {externalPageUrl && isActive && (
                      <div className="mt-HeroDealer-containerWeb">
                        <IconLinkExternal />
                        <div>
                          <a
                            className="mt-HeroDealer-containerInfoWebsite"
                            href={externalPageUrl}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {i18n.t('COMMON.VISIT_WEB')}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {(dealerImage || Boolean(videos.length)) && (
              <div className="mt-HeroDealer-media">
                <DealerImagePreview dealerId={id} image={dealerImage} videos={videos} name={name} />
              </div>
            )}
          </div>
          {isDefaultType &&
            (isDesignDefault ? (
              <ContactButtons
                adId={adId}
                dealer={dealer}
                extendedLeadTrackingData={extendedLeadTrackingData}
                isFavorite={isFavorite}
                isShareDealerEnabled={isShareDealerEnabled}
                isHidePhone={isHidePhone}
                isLoggedUser={isLoggedUser}
                multiProvinceTrackingData={multiProvinceTrackingData}
                onClickOpenModal={onClickOpenModal}
                pageName={pageName}
              />
            ) : (
              <PanelContactButtons
                adCategoryId={adCategoryId}
                adId={adId}
                dealer={dealer}
                extendedLeadTrackingData={extendedLeadTrackingData}
                isRentingDealer={isRentingDealer}
                isFavorite={isFavorite}
                isHidePhone={isHidePhone}
                isLoggedUser={isLoggedUser}
                multiProvinceTrackingData={multiProvinceTrackingData}
                linkFactory={Link}
                pageName={pageName}
              />
            ))}
        </div>
      </AtomPanel>
    )
  }
)

HeroDealer.displayName = 'HeroDealer'
HeroDealer.propTypes = {
  adCategoryId: PropTypes.number,
  adId: PropTypes.string,
  dealer: PropTypes.shape({
    hasCallTrackingService: PropTypes.bool,
    id: PropTypes.string,
    name: PropTypes.string,
    pack: PropTypes.string,
    phoneNumber: PropTypes.string,
    logoImageUrl: PropTypes.string,
    location: PropTypes.shape({
      address: PropTypes.string,
      city: PropTypes.string,
      province: PropTypes.string,
      provinceId: PropTypes.string,
      postalCode: PropTypes.string,
      coordinates: PropTypes.shape({
        lat: PropTypes.string,
        lng: PropTypes.string
      }),
      mapsDirectionsUrl: PropTypes.string
    }),
    isActive: PropTypes.bool,
    externalPageUrl: PropTypes.string,
    storeImageUrls: PropTypes.array,
    ratings: PropTypes.shape({
      average: PropTypes.number,
      tags: PropTypes.arrayOf(PropTypes.string),
      totalReviews: PropTypes.number
    })
  }).isRequired,
  design: PropTypes.oneOf(Object.values(DESIGN)),
  extendedLeadTrackingData: PropTypes.object,
  isFavorite: PropTypes.bool,
  isShareDealerEnabled: PropTypes.bool,
  isHidePhone: PropTypes.bool,
  isLoggedUser: PropTypes.bool,
  isRentingDealer: PropTypes.bool,
  multiProvinceTrackingData: PropTypes.string,
  linkFactory: PropTypes.func,
  onClickGoToRatings: PropTypes.func,
  onClickOpenModal: PropTypes.func,
  pageName: PropTypes.string,
  titleAsLink: PropTypes.bool,
  type: PropTypes.oneOf(Object.values(TYPE))
}

export {TYPE as heroDealerTypes, DESIGN as heroDealerDesigns}
export default HeroDealer
